<template>
  <a
    v-if="isExternalLink"
    :href="href"
    :class="buttonClasses"
    v-on="$listeners"
  >
    <slot />
  </a>

  <router-link
    v-else-if="href"
    :to="href"
    :class="buttonClasses"
    v-on="$listeners"
    @click.native="scrollFix(href)"
  >
    <slot />
  </router-link>

  <div
    v-else
    class="relative"
  >
    <button
      :disabled="loading || disabled"
      class="w-full"
      :class="buttonClasses"
      :type="type"
      v-on="$listeners"
    >
      <slot />
    </button>

    <div
      class="absolute top-0 left-0 flex-col items-center justify-center w-full h-full"
      :class="[loading ? 'flex' : 'hidden']"
    >
      <span class="text-sm text-center loading-text">Loading</span>
      <div class="relative flex-shrink-0 w-full h-3 mt-1 lh-loading-animation bullets-3">
        <div class="absolute top-0 w-3 h-3 lh-loading lh-loading_1" />
        <div class="absolute w-3 h-3 lh-loading lh-loading_2" />
        <div class="absolute w-3 h-3 lh-loading lh-loading_3" />
      </div>
    </div>
  </div>
</template>

<script>
import { startsWith } from 'lodash'

import Vue from 'vue'
import getConfig from '~branding'

export default Vue.extend({
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (val) => ['primary', 'secondary', 'secondary-border'].includes(val),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    href: {
      type: [String, Object],
      default: '',
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: undefined,
    },
  },
  computed: {
    isExternalLink() {
      if (typeof (this.href) === 'string') {
        return startsWith(this.href, 'http')
      }
      return false
    },
    buttonClasses() {
      return [
        'text-lg border tracking-wide appearance-none leading-none h-13 inline-flex items-center transition-colors duration-200 ease-in-out justify-center px-6 focus:outline-none relative font-bold rounded-full font-lufthansa-head',
        `btn-${this.variant}`,
        this.disabled ? 'disabled' : '',
        this.loading ? 'opacity-30 disabled' : '',
        this.widthClasses,
      ]
    },
    toHashtag() {
      if (typeof (this.href) === 'string') {
        return this.href.charAt(0) === '#'
      }
      return false
    },
    widthClasses() {
      return [
        this.fullWidth ? 'w-full' : '',
      ]
    },
  },
  methods: {
    // So that second use of the same hashtag triggers the url change (https://forum.vuejs.org/t/how-to-handle-anchors-bookmarks-with-vue-router/14563)
    scrollFix(hashtag) {
      if (this.toHashtag) {
        window.scrollTo(0, document.querySelector(hashtag).offsetTop - getConfig().header.offset)
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@mixin button-focus {
  content: '';
  position: absolute;
  left: -4px;
  top: -4px;
  width: calc(100% + 8px);
  height: calc(100% + 8px);
  border: 1px transparent solid;

  @apply rounded-full;
}

.btn-primary {
  @apply text-4y-nightsky-blue bg-4y-sonny-yellow border-transparent;

  &:focus {
    &::after {
      @include button-focus;
      @apply border-4y-sonny-yellow;
    }
  }
}

.btn-secondary {
  @apply text-4y-sky-blue bg-white border-4y-sky-blue border-2;

  &:hover {
    @apply bg-4y-sky-blue text-white;
  }

  &:focus {
    &::after {
      @include button-focus;
      @apply border-4y-sky-blue;
    }
  }
}

button.disabled {
  @apply bg-4y-gray-200 text-4y-gray-900 cursor-not-allowed;
}

.lh-loading-animation {
  &.bullets-3 {
    width: 65px;
  }

  .lh-loading {
    position: absolute;
    top: 0;
    width: 12px;
    height: 12px;
    animation-name: bounce_lh-loading;
    -o-animation-name: bounce_lh-loading;
    -ms-animation-name: bounce_lh-loading;
    -webkit-animation-name: bounce_lh-loading;
    -moz-animation-name: bounce_lh-loading;
    animation-duration: 1.5s;
    animation-iteration-count: infinite;
    animation-direction: normal;
    border-radius: 12px;
    transform: scale(0.6);
  }

  .lh-loading_1 {
    left: 0;
    animation-delay: 0.6s;
  }

  .lh-loading_2 {
    left: 25px;
    animation-delay: 0.75s;
  }

  .lh-loading_3 {
    left: 50px;
    animation-delay: 0.9s;
  }
}

@keyframes bounce_lh-loading {
  0% {
    transform: scale(0.6);
    background-color: gainsboro;
  }

  33% {
    transform: scale(1);
    background-color: #e5002b;
  }

  100% {
    transform: scale(0.6);
    background-color: gainsboro;
  }
}
</style>
